import type { LucideIcon } from 'lucide-react'
import { Shield, Sparkles, TrendingUp, Zap } from 'lucide-react'
import type { Address } from 'viem'
import type { EulerIndexerProduct } from '@/domain/euler/elends/types'
import { APP_ORIGIN } from '@/config/consts'

/**
 * Which opportunity `source` this product's vaults flow through in the unified
 * earn pipeline. `elend` → Euler v2 lending clusters (Prime / Yield / Scale /
 * etc.); `evault` → curated meta-vaults (the purr* series). Used to derive
 * filter chips without hardcoding cluster names.
 */
export type ProductSource = 'elend' | 'evault'

/**
 * UI-level configuration for a product that should appear as a cluster tile on
 * the Markets Overview page. Colocated on the product so adding a new cluster
 * only requires editing `HYPEREVM_PRODUCTS`, not a parallel registry.
 */
export interface ProductClusterUi {
  /** Short display name, e.g. "Prime" / "Scale" / "Earn". */
  name: string
  /** One-line subtitle shown on the cluster tile. */
  description: string
  /** Longer marketing copy shown when the cluster is expanded. */
  details: string
  icon: LucideIcon
  background: string
  foreground: string
  selectedBorder: string
}

export interface Product {
  name: string
  description?: string
  entity: string | string[]
  url?: string
  vaults: Address[]
  isGovernanceLimited?: boolean
  deprecatedVaults?: Address[]
  source: ProductSource
  /**
   * Optional cluster-tile UI config. Present for products that appear as a
   * cluster on the Markets Overview page; absent for products that should only
   * appear in detail/list views.
   */
  cluster?: ProductClusterUi
}

export type Products = Map<string, Product>

/**
 * Products (Markets) for HyperEVM (999)
 *
 * A "Market" in Euler is a grouping of vaults managed by certain entities.
 */
export const HYPEREVM_PRODUCTS: Products = new Map([
  [
    'hypurrfi-earn',
    {
      name: 'HypurrFi Earn Vault',
      description: 'Earn vaults curated by Clearstar for HypurrFi on HyperEVM.',
      entity: ['clearstar'],
      url: APP_ORIGIN,
      source: 'evault',
      cluster: {
        name: 'Vault',
        description: 'Curated DeFi yield vaults',
        details:
          'Curated by Clearstar with institutional-grade risk management. Deposits are routed across underlying lending strategies on HypurrFi to optimize yield without active management.',
        icon: Sparkles,
        background: 'bg-gradient-yellow/15',
        foreground: 'text-gradient-yellow',
        selectedBorder: 'border-gradient-yellow/30',
      },
      // Canonical earn (eEarn) vaults on HyperEVM (999)
      vaults: [
        '0xf38ea9de758a8f6be08b6e65bc0ff2f3e3ab741b', // purrUSDH
        '0xe8b10461ea0b04ff30f4cbfc3e93957cac00ded4', // purrHYPE (WHYPE underlying)
        '0x6dd448d5cb73dc96788d5be605dd3c5c83864a36', // purrUSDT0
        '0xf868a2b30854fe13e26f7ab7a92609ccb6b9c0e1', // purrUSDC
        '0xEbbb1e5Dd7E9C4DCa7B7fD4442571cAE5Fb37629', // purrUSDG0
      ],
    },
  ],
  [
    'hypurrfi-prime',
    {
      name: 'HypurrFi Prime',
      description: 'Fully cross-collateralized cluster for highly liquid assets on HyperEVM',
      entity: ['clearstar'],
      url: APP_ORIGIN,
      source: 'elend',
      cluster: {
        name: 'Prime',
        description: 'Euler v2 Isolated Lending Market',
        details:
          'Euler lending markets built around high-quality, liquid assets. Each vault is isolated with strict collateral standards and disciplined risk parameters.',
        icon: Shield,
        background: 'bg-[#FFE66A]/15',
        foreground: 'text-[#FFE66A]',
        selectedBorder: 'border-[#FFE66A]/30',
      },
      vaults: [
        '0xf73c654d468f5485bf15f3470b78851a49257704', // WHYPE
        '0x443100d1149d6d925edb044248bbe32c5c7ae955', // kHYPE
        '0x8a4545827df5446ba120b904e5306e58acca4e89', // UBTC
        '0xc200aab602cd7046389b5c8fb088884323f8dd0f', // USDC
        '0x28fca2611d1dd8109c26f748cd2cf3bb4fc6d2cd', // USDT0
        '0x83c34784e355ad2670db77623b845273844fa480', // USDH
        '0x8D0Dc42992597dc4e46dFCB74990341FD5BaEE9d', // USDG0
      ],
    },
  ],
  [
    'hypurrfi-yield',
    {
      name: 'HypurrFi Yield',
      description: 'Yield-bearing HYPE leverage strategies, isolated from HypurrFi Prime Market',
      entity: ['clearstar'],
      url: APP_ORIGIN,
      source: 'elend',
      cluster: {
        name: 'Yield',
        description: 'Euler v2 Isolated Lending Market',
        details:
          'Isolated Euler markets accepting a broader range of collateral including LSTs, stablecoins, and emerging Hyperliquid assets. Wider asset exposure with individually managed vault parameters.',
        icon: TrendingUp,
        background: 'bg-[#FFB1B5]/15',
        foreground: 'text-[#FFB1B5]',
        selectedBorder: 'border-[#FFB1B5]/30',
      },
      vaults: [
        '0xc7e7861352df6919e7152c007832c48a777f2a4c', // WHYPE
        '0x97d30b40048ba3fc6b6628ce5e02e77f35b64fe0', // kHYPE
        '0x3403176f548400772c39e64564f2b148bcdfb65e', // PT-kHYPE
        '0x64a3052570f5a1c241c6c8cd32f8f9ad411e6990', // wstHYPE
        '0x1739105522e4fc9675f857c859223d24dfe7593c', // lstHYPE
        '0xcAAA9A6e543b9af588Dce91E6c35Cb5fa1c7734C', // beHYPE
        '0x61Cb3b093b7125D593CCfa135C6e4E9D52D2e697', // UBTC
        '0x06bf901Ce21450Bab46ceA74C4Bb6F07E6859CD6', // UETH
        '0x09a6ad87Eff280755BdF3E2C863358D27d81262D', // USDH
        '0x94F5C76A93F12057d73991AE5B4f70e9287b5b28', // USDT0
        '0xf9bb65e113418292d1a3555515fbd64637a0be18', // USDC
        '0xBb7DC37dbc108d40BcdD60403EF7bFDD6489071E', // whHYPE
        '0x23bf20b4d6E280eacA58826a541c9eE5401BD357', // LHYPE
        '0x38f039aA19E2759D2bb88803401F17aF5F83bb5A', // sUSN
        '0xEFC561c796AEB24D09CC35B2029881b483fc1c9e', // USDG0
      ],
    },
  ],
  [
    'hypurrfi-scale',
    {
      name: 'HypurrFi Scale',
      description: 'USDXL-centric cluster enabling borrowing against exotic HyperEVM-native collaterals',
      entity: ['clearstar'],
      url: APP_ORIGIN,
      source: 'elend',
      cluster: {
        name: 'Scale',
        description: 'Euler v2 Isolated Lending Market',
        details:
          'Isolated Euler markets powered by USDXL. Lenders earn yield with built-in downside protection: if USDXL trades below $1, lenders can buy it at a discount and repay their Pooled debt at par for a profit. Borrower demand to re-acquire USDXL for repayment provides natural pressure back toward peg.',
        icon: Zap,
        background: 'bg-[#9DD9AF]/15',
        foreground: 'text-[#9DD9AF]',
        selectedBorder: 'border-[#9DD9AF]/30',
      },
      vaults: [
        '0xd62e7c9e6b1e43cb06e1ba46243b128c81bc918c', // USDXL
        '0x0e8724954a5b2ca41146f11bd6008fbeeae50603', // PURR
        '0xb2d7b74ff64e5f4908256e1c17487939f2435155', // UFART
        '0x1ba03af962c99b36297e31f56b41be31c37452d5', // UPUMP
        '0x6e02c17eb84e2b02a4cb93c086091d50ee74c16e', // bbHLP
        '0x8231b0c73a265d745e6810bffeed83b6ed1501fb', // haHYPE
      ],
    },
  ],
])

export interface VaultMarket {
  key: string
  name: string
  description?: string
  entity: string | string[]
  url?: string
  isGovernanceLimited?: boolean
  isDeprecated?: boolean
}

/**
 * Convert indexer products to a VaultMarket.
 * Uses the first product entry if available.
 */
function fromIndexerProducts(indexerProducts?: EulerIndexerProduct[]): VaultMarket | undefined {
  if (!indexerProducts || indexerProducts.length === 0) return undefined
  const product = indexerProducts[0]!
  const name = product.name
  const key = name.toLowerCase().replace(/\s+/g, '-')
  return {
    key,
    name,
    description: product.description,
    entity: product.entity,
    isGovernanceLimited: product.isGovernanceLimited,
  }
}

/**
 * Get the market (product) for a vault address.
 *
 * Indexer-first: if `indexerProducts` are provided (from the v2/vault/list response),
 * they take priority. Falls back to the hardcoded HYPEREVM_PRODUCTS map.
 */
export function getVaultMarket(
  vaultAddress: Address,
  indexerProducts?: EulerIndexerProduct[],
): VaultMarket | undefined {
  const fromIndexer = fromIndexerProducts(indexerProducts)
  if (fromIndexer) return fromIndexer

  const normalizedVault = vaultAddress.toLowerCase()

  for (const [key, product] of HYPEREVM_PRODUCTS) {
    const isInVaults = product.vaults.some((v) => v.toLowerCase() === normalizedVault)
    const isDeprecated = product.deprecatedVaults?.some((v) => v.toLowerCase() === normalizedVault) ?? false

    if (isInVaults || isDeprecated) {
      return {
        key,
        name: product.name,
        description: product.description,
        entity: product.entity,
        url: product.url,
        isGovernanceLimited: product.isGovernanceLimited,
        isDeprecated,
      }
    }
  }

  return undefined
}

/**
 * Format curator names for display in borrow markets
 * Handles collateral and liability curators with smart deduplication
 */
export function formatCuratorsDisplay(
  collateralCurator: string | undefined,
  liabilityCurator: string | undefined,
  fallbackText: string,
): string {
  // Normalize curator names (trim and lowercase for comparison)
  const normalizedCollateral = collateralCurator?.trim().toLowerCase()
  const normalizedLiability = liabilityCurator?.trim().toLowerCase()

  // If neither curator exists, show fallback
  if (!collateralCurator && !liabilityCurator) {
    return fallbackText
  }

  // If both curators exist
  if (collateralCurator && liabilityCurator) {
    // If they're the same (case-insensitive), show once
    if (normalizedCollateral === normalizedLiability) {
      return `by ${collateralCurator}`
    }
    // If different, show both
    return `by ${collateralCurator} & ${liabilityCurator}`
  }

  // Only one curator exists - show just that curator
  if (collateralCurator) {
    return `by ${collateralCurator}`
  }
  return `by ${liabilityCurator}`
}

/**
 * Format market names for display in borrow markets
 * Handles collateral and liability markets with smart deduplication
 * Returns null if no markets exist
 */
export function formatMarketsDisplay(
  collateralMarket: VaultMarket | undefined,
  liabilityMarket: VaultMarket | undefined,
): { text: string; isMultiple: boolean } | null {
  // If neither market exists, return null
  if (!collateralMarket && !liabilityMarket) {
    return null
  }

  // If both markets exist
  if (collateralMarket && liabilityMarket) {
    // If they're the same market (compare by key, case-insensitive), show once
    if (collateralMarket.key.toLowerCase() === liabilityMarket.key.toLowerCase()) {
      return {
        text: collateralMarket.name,
        isMultiple: false,
      }
    }
    // If different, show both
    return {
      text: `${collateralMarket.name} & ${liabilityMarket.name}`,
      isMultiple: true,
    }
  }

  // Only one market exists - show just that market
  if (collateralMarket) {
    return {
      text: collateralMarket.name,
      isMultiple: false,
    }
  }
  if (liabilityMarket) {
    return {
      text: liabilityMarket.name,
      isMultiple: false,
    }
  }
  return null
}
